import { InfoTip, StatCard } from '@wundo/ui';
import type { MetricStatsResponse } from '@/lib/dashboard.ts';
import { InfoText } from './InfoText.tsx';
import { METRIC_INFO } from './metricsInfo.ts';

/** The four numbers at the top of the metrics history: activation, NPS and sessions. */
export function MetricsHeadline({ stats }: { stats: MetricStatsResponse | undefined }) {
  const activation = stats?.activation;
  const activatedPercent =
    activation && activation.total > 0
      ? Math.round((activation.activated / activation.total) * 100)
      : null;

  const nps = stats?.nps;
  const npsHint =
    nps === undefined
      ? undefined
      : `${nps.average === null ? '' : `avg ${nps.average}/10 · `}${nps.responses} responses in period`;

  return (
    <div className='grid grid-cols-4 gap-6 max-agenda:grid-cols-2 max-mobile:grid-cols-1'>
      <StatCard
        label={statLabel('Activated', METRIC_INFO.activated)}
        value={activatedPercent === null ? '—' : `${activatedPercent}%`}
        hint={
          activation &&
          `${activation.activated} of ${activation.total} users have used the app`
        }
      />
      <StatCard
        label={statLabel('NPS', METRIC_INFO.nps)}
        value={formatNpsScore(nps?.score)}
        hint={npsHint}
      />
      <StatCard
        label={statLabel('Sessions per User / Week', METRIC_INFO.sessionsWeek)}
        value={formatStat(stats?.sessions.per_messaging_user_per_week)}
        hint={stats && `${stats.sessions.total} sessions in period`}
      />
      <StatCard
        label={statLabel('Sessions per User / Month', METRIC_INFO.sessionsMonth)}
        value={formatStat(stats?.sessions.per_messaging_user_per_month)}
      />
    </div>
  );
}

function formatStat(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : String(value);
}

function formatNpsScore(score: number | null | undefined): string {
  if (score === null || score === undefined) {
    return '—';
  }
  return score > 0 ? `+${score}` : String(score);
}

/** A label with the sentence behind it, for a `StatCard` that needs one. */
function statLabel(text: string, info: string) {
  return (
    <span className='flex items-center gap-1'>
      {text}
      <InfoTip content={<InfoText text={info} />} label={text} />
    </span>
  );
}
